import { CANVAS_SIZE } from "@/features/draw/constants/canvas";
import { useAvatarStore } from "@/features/draw/stores/useAvatarStore";
import { hexToInt, intToHex } from "../utils/color/colorUtils";
import i18n from "@/locales";

/**
 * 泛洪填充算法 - 使用广度优先搜索(BFS)
 * 比原来的深度优先搜索(DFS)更高效，尤其对于大面积填充
 * 使用队列实现，避免栈溢出风险
 */
export const floodFill = (
  startIndex: number,
  targetColor: number,
  replacementColor: number,
  pixels: Uint32Array
) => {
  // 如果起始点颜色不是目标颜色或者替换颜色与目标颜色相同，则不进行填充
  if (pixels[startIndex] !== targetColor || targetColor === replacementColor) {
    return pixels;
  }

  const newPixels = new Uint32Array(pixels);

  const width = CANVAS_SIZE;
  const height = CANVAS_SIZE;
  const queue: number[] = [startIndex];
  const maxQueueSize = 10000; // 设置队列的最大长度限制

  while (queue.length > 0) {
    // 限制队列大小，防止内存溢出
    if (queue.length > maxQueueSize) {
      console.warn(i18n.global.t("Message.queueExceedLimit"));
      break;
    }

    const currentIndex = queue.shift()!; // 使用shift()从队列前端取出元素
    if (newPixels[currentIndex] !== targetColor) continue;

    newPixels[currentIndex] = replacementColor;

    const x = currentIndex % width;
    const y = Math.floor(currentIndex / width);

    // Check adjacent pixels (up, down, left, right)
    if (y > 0 && newPixels[currentIndex - width] === targetColor) {
      queue.push(currentIndex - width);
    }
    if (y < height - 1 && newPixels[currentIndex + width] === targetColor) {
      queue.push(currentIndex + width);
    }
    if (x > 0 && newPixels[currentIndex - 1] === targetColor) {
      queue.push(currentIndex - 1);
    }
    if (x < width - 1 && newPixels[currentIndex + 1] === targetColor) {
      queue.push(currentIndex + 1);
    }
  }

  return newPixels;
};

// 获取画笔影响的像素
const getBrushPixels = (
  centerIndex: number,
  brushSize: number,
  canvasSize: number
): number[] => {
  const indices: number[] = [];
  const centerX = centerIndex % canvasSize;
  const centerY = Math.floor(centerIndex / canvasSize);
  const radius = Math.floor(brushSize / 2);

  for (let y = -radius; y <= radius; y++) {
    for (let x = -radius; x <= radius; x++) {
      const newX = centerX + x;
      const newY = centerY + y;

      // 确保在画布范围内
      if (newX >= 0 && newX < canvasSize && newY >= 0 && newY < canvasSize) {
        // 计算到中心的距离
        const distance = Math.sqrt(x * x + y * y);

        // 如果在半径内，添加到影响的像素中
        if (distance <= radius) {
          indices.push(newY * canvasSize + newX);
        }
      }
    }
  }

  return indices;
};

export function useDrawingToolsWithStore() {
  const store = useAvatarStore();

  // 处理像素点击
  const handlePixelClick = (
    index: number,
    newPixels?: Uint32Array,
    isDragOperation: boolean = false
  ) => {
    const selectedLayerId = store.selectedLayerId;
    const selectedTool = store.selectedTool;
    const currentColor = store.currentColor;
    const brushSize = store.brushSize;

    const layer = store.layers.find((l) => l.id === selectedLayerId);
    if (!layer) return;

    // 处理拖动操作
    if (index === -1 && newPixels) {
      // For batch updates during drawing/erasing, only add to history when not dragging
      store.updateLayerPixels(selectedLayerId, newPixels, !isDragOperation);
      return;
    }

    // 处理吸管工具
    if (selectedTool === "eyedropper") {
      const pickedColor = layer.pixels[index];
      if (pickedColor) {
        store.currentColor = intToHex(pickedColor, false, true);
      }
      return pickedColor;
    }

    // 处理填充工具
    if (selectedTool === "fill") {
      const targetColor = layer.pixels[index];
      const newPixels = floodFill(
        index,
        targetColor,
        hexToInt(currentColor),
        layer.pixels
      );
      // Always add fill operations to history
      store.updateLayerPixels(selectedLayerId, newPixels, false);
      return;
    }

    // 处理画笔工具
    if (selectedTool === "brush") {
      const updatedPixels = new Uint32Array(layer.pixels);
      const affectedIndices = getBrushPixels(index, brushSize, CANVAS_SIZE);

      affectedIndices.forEach((pixelIndex) => {
        updatedPixels[pixelIndex] = hexToInt(currentColor);
      });

      // For single brush clicks, always add to history unless it's part of a drag
      store.updateLayerPixels(selectedLayerId, updatedPixels, !isDragOperation);
      return;
    }

    // 处理橡皮擦工具
    if (selectedTool === "eraser") {
      const updatedPixels = new Uint32Array(layer.pixels);
      const affectedIndices = getBrushPixels(index, brushSize, CANVAS_SIZE);

      affectedIndices.forEach((pixelIndex) => {
        updatedPixels[pixelIndex] = 0;
      });

      // For single eraser clicks, always add to history unless it's part of a drag
      store.updateLayerPixels(selectedLayerId, updatedPixels, !isDragOperation);
      return;
    }
  };

  // 处理拖动结束
  const handleDragEnd = () => {
    store.addHistoryEntry({
      tool: store.selectedTool,
    });
  };

  return {
    floodFill,
    handlePixelClick,
    handleDragEnd,
  };
}
